import React, { useState, useEffect } from 'react';
import { Typography, Stack, Modal, Box, IconButton, Button, CircularProgress } from '@mui/material';
import CloseModalIcon from '../../icons/RoundCloseIcon.svg';
import ResetPasswordIcon from "../../icons/ResetPasswordIcon.svg";
import WhiteInosio from "../../icons/whiteinosio.svg"
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from '@mui/styles';
import { updateUserDetails, selectAuthState, clearUserDetailsResult, getPasswordLogin } from "../sagas/auth/authSlice";
import { UpdateUserDetailsDto } from "@inosio/payments-sdk";
import { ModalTypes } from './profileSettings';
import { EmailRegex, PasswordRegex } from '../../utils/regex';

const useStyles = makeStyles({
    globalError: {
        display: "flex",
        flexDirection: "row",
        borderTop: "2px  solid #DB3939",
        borderRadius: '24px',
        padding: "12px 20px",
        width: '340px',
        height: '49px',
    },
    input: {
        border: 'none',
        outline: 'none',
        background: '#FFFFFF',
        boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
        boxSizing: 'border-box',
        borderRadius: '24px',
        height: '49px',
        maxWidth: '340px',
        width: '100%',
        padding: '0 18px',
        fontFamily: 'Work Sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '22px',
        color: '#000000',
        marginBottom: '7px !important',
    },
    inputContainer: {
        maxWidth: '340px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    }
});

const enum ModalStates {
    UpdatePage = 'Update Page',
    SuccessPage = 'Success Page',
}

interface PropsType {
    open: boolean,
    modalType: ModalTypes,
    handleClose: () => void
}

const ResetLoginInfoModal = (props: PropsType) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { isUpdatingUserDetails, isUpdateUserDetailsSuccess, updateUserDetailsError } = useSelector(selectAuthState);

    const [modalState, setModalState] = useState(ModalStates.UpdatePage);
    const [email, setEmail] = useState('');
    const [oldPassword, setOldPassword] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [emailError, setEmailError] = useState('');
    const [oldPassError, setOldPassError] = useState('');
    const [passError, setPassError] = useState('');
    const [confirmPassError, setConfirmPassError] = useState('');
    const [isUpdated, setIsUpdated] = useState(false);
    const [globalError, setGlobalError] = useState('');

    useEffect(() => {
        if (props?.modalType === ModalTypes.Email) setIsUpdated(!!email && !!oldPassword);
        if (props?.modalType === ModalTypes.Password) setIsUpdated(!!oldPassword && !!password && !!confirmPassword);
    }, [email, oldPassword, password, confirmPassword]);

    const validateEmail = () => {
        if (!email) {
            setEmailError("Email address is empty!");
            return false;
        } else if (!EmailRegex.test(email)) {
            setEmailError("Invalid email address!");
            return false;
        }
        setEmailError("");
        return true;
    };

    const validateOldPassword = () => {
        if (oldPassword.length === 0) {
            setOldPassError("Current Password is empty!");
            return false;
        } else {
            setOldPassError("");
            return true;
        }
    };

    const validatePassword = () => {
        if (password.length === 0) {
            setPassError('New Password is empty!')
            return false;
        } else if (!PasswordRegex.test(password)) {
            setPassError('Password should have 8 characters min, 1 number, 1 upper case and special character');
            return false;
        }
    
        setPassError('')
        return true;
    }
    
    const validateConfirmPassword = () => {
        if (confirmPassword !== password) {
            setConfirmPassError('Passwords don\'t not match')
            return false;
        }
    
        setConfirmPassError('')
        return true;
    }

    const handleSave = () => {
        setGlobalError('');

        let validity: boolean = false;
        if (props?.modalType === ModalTypes.Email) validity = validateEmail() && validateOldPassword();
        if (props?.modalType === ModalTypes.Password) validity = validateOldPassword() && validatePassword() && validateConfirmPassword();

        if (validity) {
            dispatch({type: clearUserDetailsResult.type});
            
            dispatch(
                updateUserDetails({
                    email: email || undefined,
                    oldPassword: oldPassword || undefined,
                    password: password || undefined,
                } as UpdateUserDetailsDto)
            );
        }
    }

    const handleClose = () => {
        props.handleClose();
        setModalState(ModalStates.UpdatePage);
        setEmail('');
        setOldPassword("");
        setPassword("");
        setConfirmPassword("");
        setEmailError('');
        setOldPassError('');
        setPassError('');
        setConfirmPassError('');
        setIsUpdated(false);
        setGlobalError('');
        dispatch({type: clearUserDetailsResult.type});
    }

    useEffect(() => {
        if (isUpdateUserDetailsSuccess) {
            setModalState(ModalStates.SuccessPage);

            //So important...If email updated we update the ipaAccessToken...
            if (email) {
                dispatch({
                    type: getPasswordLogin.type,
                    payload: {
                        email: email,
                        password: oldPassword,
                    },
                });
            }
        }

        if (updateUserDetailsError) {
            if (updateUserDetailsError?.statusCode === 401) {
                setOldPassError("Current Password is incorrect!");
            } else if (updateUserDetailsError?.statusCode === 409) {
                setEmailError("Email address is already taken!");
            } else {
                setGlobalError('Something went wrong. Please try again later.');
            }
        }
    }, [isUpdateUserDetailsSuccess, updateUserDetailsError]);

    return (
        <div>
            <Modal
                open={props.open}
                onClose={handleClose }
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description">
                <Box sx={{
                    position: 'absolute' as 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 472,
                    minHeight: 523,
                    background: '#FFFFFF',
                    boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
                    borderRadius: '24px',
                    p: 4,
                    outline: 'none',
                }}>
                    <Stack 
                        direction="column"
                        justifyContent="start"
                        alignItems="space-between"
                        spacing={1}
                    >
                        <Stack 
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            spacing={2}
                            sx={{ position: 'relative' }}
                        >
                            <Typography sx={{
                                fontFamily: '"Work Sans",Sans serif',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                fontSize: '18px',
                                lineHeight: '22px',
                                color: '#14142B',
                                margin: "0 !important"
                            }}>
                                {modalState === ModalStates.UpdatePage ? `Update ${props?.modalType}`: `${props?.modalType} updated`}
                            </Typography>

                            <IconButton
                                edge="start"
                                color="inherit"
                                aria-label="Close modal"
                                onClick={handleClose}
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                }}
                            >
                                <img src={CloseModalIcon} alt="close" />
                            </IconButton>
                        </Stack>

                        <Stack
                            justifyContent="center"
                            alignItems="center"
                            spacing={1}
                            sx={{ 
                                minHeight: 420,
                            }}
                        >
                            {modalState === ModalStates.UpdatePage ? (
                               <React.Fragment>
                                    {globalError?.length ?
                                        <div className={classes.globalError} >
                                            <Typography
                                                style={{
                                                    fontFamily: "'Open Sans', sans-serif",
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    fontSize: '14px',
                                                    lineHeight: '18px',
                                                    color: '#DB3939'
                                                }}>
                                                {globalError}
                                            </Typography>
                                        </div> : ""
                                    }
                                    <img src={WhiteInosio} style={{ width: "78px", height: "78px", marginTop: '20px' }} alt="logo" />
                                    <Typography sx={{
                                        fontFamily: "Work Sans",
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '18px',
                                        lineHeight: '21px',
                                        textAlign: 'center',
                                        color: '#14142B',
                                        margin: '17px 0 !important',
                                    }}>
                                        {`Please enter your new ${props?.modalType?.toLocaleLowerCase()}`}
                                    </Typography>

                                    <Box className={classes.inputContainer} sx={{ display: props?.modalType === ModalTypes.Password ? 'none': '' }}>
                                        <input
                                            id='email'
                                            placeholder="Email"
                                            value={email}
                                            autoComplete="email"
                                            onChange={(e) => setEmail(e.target.value)}
                                            className={classes.input}
                                        />

                                        {emailError.length ? <Typography
                                            style={{
                                                fontFamily: "Work Sans",
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '18px',
                                                color: '#DB3939',
                                                marginLeft: '18px',
                                            }}>
                                            {emailError}
                                        </Typography> : <></>}
                                    </Box>

                                    <Box className={classes.inputContainer}>
                                        <input
                                            id='oldPassword'
                                            type='password'
                                            placeholder="Current Password"
                                            value={oldPassword}
                                            autoComplete="old-password"
                                            onChange={(e) => setOldPassword(e.target.value)}
                                            className={classes.input}
                                        />

                                        {oldPassError.length ? <Typography
                                            style={{
                                                fontFamily: "Work Sans",
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '18px',
                                                color: '#DB3939',
                                                marginLeft: '18px',
                                            }}>
                                            {oldPassError}
                                        </Typography> : <></>}
                                    </Box>

                                    <Box className={classes.inputContainer} sx={{ display: props?.modalType === ModalTypes.Email ? 'none': '' }}>
                                        <input
                                            id='password'
                                            type='password'
                                            placeholder="New Password"
                                            value={password}
                                            autoComplete="new-password"
                                            onChange={(e) => setPassword(e.target.value)}
                                            className={classes.input}
                                        />

                                        {passError.length ? <Typography
                                            style={{
                                                fontFamily: "Work Sans",
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '18px',
                                                color: '#DB3939',
                                                marginLeft: '18px',
                                            }}>
                                            {passError}
                                        </Typography> : <></>}
                                    </Box>

                                    <Box className={classes.inputContainer} sx={{ display: props?.modalType === ModalTypes.Email ? 'none': '' }}>
                                        <input
                                            id='confirmPassword'
                                            type='password'
                                            placeholder="Confirm your new password"
                                            value={confirmPassword}
                                            autoComplete="confirm-password"
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={classes.input}
                                        />

                                        {confirmPassError.length ? <Typography
                                            style={{
                                                fontFamily: "Work Sans",
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                fontSize: '14px',
                                                lineHeight: '18px',
                                                color: '#DB3939',
                                                marginLeft: '18px',
                                            }}>
                                            {confirmPassError}
                                        </Typography> : <></>}
                                    </Box>

                                    <Box sx={{ width: '100%', maxWidth: '340px', textAlign: 'right' }}>
                                        <Button
                                            variant="contained"
                                            style={{
                                                width: "113px",
                                                marginTop: '2px',
                                                height: "49px",
                                                fontSize: '18px',
                                                borderRadius: "24px",
                                                textTransform: "capitalize",
                                                color: 'white',
                                                boxShadow: '0px 0px 40px rgba(0, 0, 0, 0.1)',
                                                backgroundColor: isUpdated ? "#39A2DB" : "rgba(211, 211, 211, 1)",
                                            }}
                                            onClick={handleSave}
                                            disabled={isUpdatingUserDetails || !isUpdated}
                                        >
                                            {isUpdatingUserDetails ? <CircularProgress size={14} sx={{ color: '#ffffff', mr: 0.5 }} />: ''}
                                            Save
                                        </Button>
                                    </Box>
                                </React.Fragment>
                            ) : modalState === ModalStates.SuccessPage ? (
                                <React.Fragment>
                                    <img src={ResetPasswordIcon} style={{ width: 148, height: 148, marginTop: 80 }} alt="" />
                                    <Typography sx={{
                                        fontFamily: "Work Sans",
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '18px',
                                        lineHeight: '21px',
                                        textAlign: 'center',
                                        color: '#000000',
                                        margin: '80px 0 50px !important',
                                    }}>
                                        Updated Successfully!
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={handleClose}
                                        style={{
                                            width: "156px",
                                            height: "42px",
                                            borderRadius: "24px",
                                            textTransform: "capitalize",
                                            color: 'white',
                                            backgroundColor: "#39A2DB",
                                            borderColor: "#39A2DB",
                                            fontFamily: 'Work Sans',
                                            fontStyle: 'normal',
                                            fontWeight: 400,
                                            fontSize: '18px',
                                            lineHeight: '21px',
                                        }}
                                    >
                                        Done
                                    </Button>
                                </React.Fragment>
                            ) : (
                                <></>
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </Modal>
        </div>
    )
}

export default ResetLoginInfoModal;
